import {h, Component, createRef, RefObject} from 'preact';
import {ui, core} from '@playkit-js/kaltura-player-js';
import {Button, ButtonType, ButtonSize} from '@playkit-js/common/dist/components/button';
import {OnClickEvent} from '@playkit-js/common/dist/hoc/a11y-wrapper';
import {AudioIcon} from '../audio-icon';
import {AadEventTriggers} from '../../types/events';

import * as styles from './aad-overlay.scss';

const {Space} = ui.utils.KeyCode;

// TODO use values from playkit-ui
const KeyR = 'KeyR';
const KeyS = 'KeyS';
const KeyK = 'KeyK';

const {
  components: {PLAYER_SIZE},
  redux: {connect},
  reducers: {shell}
} = KalturaPlayer.ui;
const {Text, withText} = KalturaPlayer.ui.preacti18n;

const HIDE_BOTTOM_BAR_CLASS = 'bottom-bar-hidden';

export interface InfoProps {
  captionContent: string;
  onReplay: (trigger: AadEventTriggers) => void;
  onSkip: (trigger: AadEventTriggers) => void;
  onPauseResume: (trigger: AadEventTriggers) => boolean;
  onScroll: () => void;
  skipButtonLabel?: string;
  replayButtonLabel?: string;
  pauseButtonLabel?: string;
  resumeButtonLabel?: string;
  fullScreenOverlay?: boolean;
  hideBottomBar?: () => void;
  showBottomBar?: () => void;
}
interface ConnectProps {
  playerSize?: string;
}

interface AadOverlayState {
  contentVisible: boolean;
  paused: boolean;
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    hideBottomBar: () => dispatch(shell.actions.addPlayerClass(HIDE_BOTTOM_BAR_CLASS)),
    showBottomBar: () => dispatch(shell.actions.removePlayerClass(HIDE_BOTTOM_BAR_CLASS))
  };
};

type MergedProps = InfoProps & ConnectProps;

const mapStateToProps = (state: Record<string, any>) => ({
  playerSize: state.shell.playerSize
});

const translates = ({}: InfoProps) => {
  return {
    skipButtonLabel: <Text id="plugin-advanced-audio-description.skip">Skip</Text>,
    replayButtonLabel: <Text id="plugin-advanced-audio-description.replay">Replay</Text>,
    pauseButtonLabel: <Text id="plugin-advanced-audio-description.pause">Pause</Text>,
    resumeButtonLabel: <Text id="plugin-advanced-audio-description.resume">Play</Text>
  };
};

class AadOverlay extends Component<MergedProps, AadOverlayState> {
  private _buttonRef: RefObject<HTMLButtonElement> = createRef();

  constructor(props: MergedProps) {
    super(props);
    this.state = {
      contentVisible: true,
      paused: false
    };
  }

  componentDidMount(): void {
    this.props.hideBottomBar!();
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount(): void {
    this.props.showBottomBar!();
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (event: KeyboardEvent) => {
    const {onReplay, onSkip} = this.props;
    if (event.code === Space || event.code === KeyK) {
      event.preventDefault();
      this.handlePauseResume(event, true, AadEventTriggers.SHORTCUT);
    }

    if (event.code == KeyS && event.shiftKey) {
      event.preventDefault();
      onSkip(AadEventTriggers.SHORTCUT);
    }

    if (event.code == KeyR && event.shiftKey) {
      event.preventDefault();
      onReplay(AadEventTriggers.SHORTCUT);
    }
  };

  handleHideContent = (e: OnClickEvent, byKeyboard: boolean) => {
    this.setState({contentVisible: false});
    if (byKeyboard) {
      this._buttonRef.current?.focus();
    }
  };

  handlePauseResume = (e: OnClickEvent, byKeyboard: boolean, trigger: AadEventTriggers) => {
    if (e.shiftKey && byKeyboard) {
      // Ignore Shift + Space
      this.props.onSkip(AadEventTriggers.SHORTCUT);
      return;
    }
    const {onPauseResume} = this.props;
    const paused = onPauseResume(trigger);
    this.setState({paused});
  };

  renderContentWrapper = () => {
    const {captionContent, onScroll} = this.props;
    if (!this.state.contentVisible) {
      return null;
    }
    return (
      <div className={styles.aadContentWrapper} data-testid="aadContentWrapper" tabIndex={-1}>
        {this.renderCloseButton()}
        <div className={styles.aadPlaybackIcon} data-testid="aadPlaybackIcon">
          <AudioIcon isActive={!this.state.paused} />
        </div>
        <div className={styles.captionContentWrapper} data-testid="aadCaptionContent" onScroll={onScroll}>
          {captionContent}
        </div>
      </div>
    );
  };

  renderButtonsWrapper = () => {
    const {skipButtonLabel, replayButtonLabel, pauseButtonLabel, resumeButtonLabel, onReplay, onSkip} = this.props;
    return (
      <div className={styles.aadButtonsWrapper} data-testid="aadButtonsWrapper">
        <div className={styles.buttonWrapper}>
          <Button
            focusOnMount
            type={ButtonType.translucent}
            size={ButtonSize.large}
            className={styles.pauseResumeButton}
            onClick={(e, byKeyboard) => this.handlePauseResume(e, byKeyboard, AadEventTriggers.BUTTON)}
            testId="aadPauseResumeButton"
            setRef={ref => (this._buttonRef.current = ref)}
          >
            {this.state.paused ? resumeButtonLabel : pauseButtonLabel}
          </Button>
          <div className={styles.buttonHint}>K</div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button type={ButtonType.translucent} size={ButtonSize.large} onClick={() => onSkip(AadEventTriggers.BUTTON)} testId="aadSkipButton">
            {skipButtonLabel}
          </Button>
          <div className={styles.buttonHint}>Shift+S</div>
        </div>
        <div className={styles.buttonWrapper}>
          <Button type={ButtonType.translucent} size={ButtonSize.large} onClick={() => onReplay(AadEventTriggers.BUTTON)} testId="aadReplayButton">
            {replayButtonLabel}
          </Button>
          <div className={styles.buttonHint}>Shift+R</div>
        </div>
      </div>
    );
  };

  renderCloseButton = () => {
    const {fullScreenOverlay} = this.props;
    if (fullScreenOverlay) {
      return null;
    }
    return (
      <Button
        icon={'close'}
        type={ButtonType.borderlessTranslucent}
        onClick={this.handleHideContent}
        className={styles.closeButton}
        testId="aadHideButton"
      />
    );
  };

  render() {
    const {playerSize = '', fullScreenOverlay} = this.props;
    if (playerSize === PLAYER_SIZE.TINY) {
      return null;
    }
    const aadRootClass = [styles.aadRoot, styles[playerSize]];
    if (fullScreenOverlay) {
      aadRootClass.push(styles.fullScreenOverlay);
    }
    return (
      <div className={aadRootClass.join(' ')} data-testid={`aadRoot${fullScreenOverlay ? '-fullScreen' : ''}`}>
        {this.renderContentWrapper()}
        {this.renderButtonsWrapper()}
      </div>
    );
  }
}

export default withText(translates)(connect(mapStateToProps, mapDispatchToProps)(AadOverlay));
